import { ARC_INTERACTION, ARC_STATUS } from '../../components/coach/arc/arcVisualSystem';

/**
 * Arc Visualization Configuration
 *
 * Central configuration for the rowing arc visualization including:
 * - Physics constants (handle radius, force calculations)
 * - Display settings (radii, colors, spacing)
 * - Animation parameters (durations, drive ratios)
 */

/**
 * Physical parameters for rowing biomechanics and force calculations.
 * These constants are used to convert between angular measurements at the gate
 * and linear measurements at the handle.
 */
export const ARC_PHYSICS = {
  /**
   * Approximate radius from gate to grip center for collegiate sweep rowing.
   * Used to convert angular displacement to linear handle travel.
   * Unit: centimeters
   */
  HANDLE_RADIUS_CM: 50,

  /**
   * Conversion factor: degrees to centimeters of handle travel.
   * Calculated as: (π / 180) × HANDLE_RADIUS_CM
   * Unit: cm per degree
   */
  CM_PER_DEGREE: (Math.PI / 180) * 50,

  /**
   * Ratio of drive phase to total stroke cycle.
   * Drive ratio of 0.45 means 45% drive, 55% recovery.
   * Typical range: 0.4-0.5 for competitive rowing.
   */
  OAR_DRIVE_RATIO: 0.45,

  /**
   * Default stroke cycle duration in milliseconds.
   * At 30 strokes/minute: 2000ms
   * At 27 strokes/minute: ~2200ms
   */
  STROKE_DURATION_MS: 2200,
} as const;

/**
 * Visual styling constants for the arc diagram.
 */
export const ARC_COLORS = {
  /**
   * Catch slip segment color (red indicates inefficiency)
   */
  catchSlip: ARC_STATUS.bad.color,

  /**
   * Finish slip segment color (yellow warns of washout)
   */
  finishSlip: ARC_STATUS.caution.color,

  /**
   * Effective length segment color (green indicates productive work)
   */
  effectiveLength: ARC_STATUS.good.color,

  /**
   * Total arc outline color (blue reference line)
   */
  totalArc: ARC_INTERACTION.activeColor,

  /**
   * Boat centerline color (dark slate)
   */
  centerline: '#0F172A',

  /**
   * Reference line color (light gray)
   */
  referenceLine: 'rgba(55, 65, 81, 0.35)',
} as const;

/**
 * Geometric display configuration for the arc visualization.
 * Controls the size, spacing, and appearance of all visual elements.
 */
export const ARC_DISPLAY = {
  /**
   * Inner radius of the arc (hub)
   * Unit: SVG pixels
   */
  innerRadius: 60,

  /**
   * Outer radius of the arc (rim)
   * Unit: SVG pixels
   */
  outerRadius: 180,

  /**
   * Whether to show the horizontal reference line at 0°
   */
  showReferenceLines: true,

  /**
   * Whether to show radial grid lines at key angles
   */
  showGrid: false,

  /**
   * Angular spacing between degree tick marks
   * Unit: degrees
   */
  degreeTickStep: 20,

  /**
   * Inner offset for degree tick marks from outer radius
   * Unit: SVG pixels
   */
  degreeTickInnerOffset: 8,

  /**
   * Outer extent of degree tick marks beyond outer radius
   * Unit: SVG pixels
   */
  degreeTickOuterOffset: 18,

  /**
   * Radius for degree label text placement
   * Unit: SVG pixels from outer radius
   */
  degreeLabelRadius: 30,

  /**
   * Offset for full circumference reference circle
   * Unit: SVG pixels beyond outer radius
   */
  circumferenceOffset: 10,

  /**
   * Horizontal spacing between legend items
   * Unit: SVG pixels
   */
  legendItemSpacing: 140,

  /**
   * Ratio of boat centerline position to outer radius
   * 0.3 means centerline is at 30% of outer radius from center
   */
  centerlineRatio: 0.3,

  /**
   * Horizontal extension of centerline beyond arc
   * Unit: SVG pixels
   */
  centerlineExtension: 200,

  /**
   * Padding between centerline end and "Bow"/"Stern" labels
   * Unit: SVG pixels
   */
  labelPadding: 40,

  /**
   * Offset for oar pivot radius beyond outer arc
   * Unit: SVG pixels
   */
  oarRadiusOffset: 10,

  /**
   * Extra length of oar handle beyond centerline
   * Unit: SVG pixels
   */
  handleExtraLength: 18,

  /**
   * Length of oar blade in water
   * Unit: SVG pixels
   */
  bladeLength: 28,

  /**
   * Width of oar blade
   * Unit: SVG pixels
   */
  bladeWidth: 12,
} as const;

/**
 * Geometric constants for arc elements.
 */
export const ARC_GEOMETRY = {
  /**
   * Offset distance for total arc outline beyond the main arc
   * Unit: SVG pixels
   */
  TOTAL_ARC_OFFSET: 6,

  /**
   * Radius of the oar pivot point circle
   * Unit: SVG pixels
   */
  OAR_PIVOT_RADIUS: 4,

  /**
   * Stroke width of the oar shaft line
   * Unit: SVG pixels
   */
  OAR_SHAFT_WIDTH: 3.6,

  /**
   * Stroke width of the boat centerline
   * Unit: SVG pixels
   */
  CENTERLINE_STROKE_WIDTH: 2.2,
} as const;

/**
 * Animation configuration for arc transitions and playback.
 */
export const ARC_ANIMATION = {
  /**
   * Default duration for enter/exit animations
   * Unit: milliseconds
   */
  duration: 800,

  /**
   * Stagger delay between animating successive elements
   * Unit: milliseconds
   */
  delay: 200,

  /**
   * Whether animations are enabled by default
   */
  enabled: true,

  /**
   * Default stroke cycle duration for playback
   * Unit: milliseconds
   */
  strokeDuration: ARC_PHYSICS.STROKE_DURATION_MS,

  /**
   * Drive phase ratio for oar animation timing
   * Should match ARC_PHYSICS.OAR_DRIVE_RATIO
   */
  driveRatio: ARC_PHYSICS.OAR_DRIVE_RATIO,
} as const;

/**
 * Force calculation constants for trend line visualization.
 * These define the force profile during different stroke phases.
 */
export const FORCE_PROFILE = {
  /**
   * Maximum gate force during catch slip phase.
   * Catch slip shows rising force as blade enters water.
   * Unit: arbitrary (scaled to peak force)
   */
  CATCH_SLIP_MAX_FORCE: 0.6,

  /**
   * Force at entry to effective length phase.
   * Marks transition from catch to drive.
   * Unit: arbitrary (scaled to peak force)
   */
  EFFECTIVE_ENTRY_FORCE: 2,

  /**
   * Force at end of finish slip phase.
   * Low force as blade exits water.
   * Unit: arbitrary (scaled to peak force)
   */
  FINISH_SLIP_END_FORCE: 0.4,

  /**
   * Epsilon for floating point comparisons in phase boundaries.
   * Used to handle rounding errors in phase timing calculations.
   */
  EPSILON: 1e-4,
} as const;

/**
 * Metrics calculation parameters.
 */
export const METRICS_CONFIG = {
  /**
   * Number of strokes to include in rolling average calculations.
   * 6 strokes provides smooth trending without excessive lag.
   */
  ROLLING_WINDOW: 6,

  /**
   * Number of strokes visible in trend line viewport.
   * Window scrolls to keep current stroke centered.
   */
  WINDOW_STROKES: 2,
} as const;

/**
 * Technical target ranges for rowing metrics.
 * Based on coaching standards (Claire's crew targets).
 */
export const TECHNICAL_TARGETS = {
  catchSlip: {
    min: 5,
    max: 8,
    label: 'Target 5–8°',
    tooltip:
      'Claire keeps the catch gap tight; slip should stay under 8° while avoiding a dead catch.',
  },
  effectiveLength: {
    min: 67, // centimeters
    label: 'Goal ≥ 67 cm',
    tooltip:
      "Claire's crews aim for at least 67 cm of effective length. Maintain a long drive while controlling slip.",
  },
  finishSlip: {
    min: 3,
    max: 7,
    label: 'Target 3–7°',
    tooltip:
      'Finish slip should stay above 3° to protect the release, but below 7° to avoid washing out.',
  },
} as const;

/**
 * Playback speed options for the arc animation.
 * Allows coaches to review strokes at different speeds.
 */
export const SPEED_OPTIONS = [0.25, 0.5, 1, 1.5, 2] as const;
